import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { TextField, Button, FormControlLabel, Checkbox } from '@mui/material';
import './InformativoForm.css';

export default function InformativoForm({ informativo, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isIndefinite: true,
    expiresAt: '',
    images: []
  });

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (informativo) {
      setFormData({
        title: informativo.title || '',
        description: informativo.description || '',
        isIndefinite: informativo.isIndefinite || false,
        expiresAt: informativo.expiresAt ? informativo.expiresAt.split('T')[0] : '',
        images: informativo.images || []
      });
    }
  }, [informativo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
      ...(name === 'isIndefinite' && checked && { expiresAt: '' })
    }));
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (let file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', file);

        const res = await api.post('/informativos/upload-image', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (res.data.success) {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, res.data.fileName]
          }));
          toast.success('Imagem enviada com sucesso!');
        }
      }
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (fileName) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== fileName)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    if (!formData.isIndefinite && !formData.expiresAt) {
      toast.error('Data de expiração é obrigatória se não for indeterminado');
      return;
    }

    setSubmitting(true);
    try {
      const dataToSend = {
        title: formData.title,
        description: formData.description,
        isIndefinite: formData.isIndefinite,
        expiresAt: formData.expiresAt || null,
        images: formData.images
      };

      await onSave(dataToSend);
    } finally {
      setSubmitting(false);
    }
  };

  const apiBase = process.env.REACT_APP_URL_BASE || '';
  const apiOrigin = apiBase.replace(/\/api\/?$/i, '').replace(/\/$/, '');

  return (
    <div className="form-modal-overlay" onClick={onClose}>
      <div className="form-modal card" onClick={e => e.stopPropagation()}>
        <div className="card-body">
          <div className="modal-header">
            <h3>{informativo ? 'Editar Informativo' : 'Novo Informativo'}</h3>
            <button onClick={onClose} className="btn btn-secondary close-btn">✕</button>
          </div>

          <form onSubmit={handleSubmit} className="informativo-form">
            {/* Título */}
            <TextField
              label="Título"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
            />

            {/* Descrição */}
            <TextField
              label="Descrição"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              sx={{ mb: 2 }}
            />

            {/* Expiração */}
            <div className="expiration-section">
              <FormControlLabel
                control={
                  <Checkbox
                    name="isIndefinite"
                    checked={formData.isIndefinite}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Expiração indeterminada"
              />

              {!formData.isIndefinite && (
                <TextField
                  type="date"
                  name="expiresAt"
                  value={formData.expiresAt}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 1 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </div>

            {/* Upload de mídia */}
            <div className="images-section">
              <h4>Mídia (Imagens e PDFs)</h4>

              {/* Input de upload */}
              <div className="upload-area">
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  id="image-input"
                  style={{ display: 'none' }}
                />
                <label htmlFor="image-input" className="upload-label">
                  {uploading ? 'Enviando...' : 'Clique para adicionar mídia (imagens ou PDF)'}
                </label>
              </div>

              {/* Preview de mídia */}
              {formData.images.length > 0 && (
                <div className="images-preview">
                  {formData.images.map((fileName, idx) => (
                    <div key={idx} className="image-item">
                      {fileName.toLowerCase().endsWith('.pdf') ? (
                        <div className="pdf-preview">
                          <div className="pdf-icon">📄</div>
                          <span className="pdf-name">{fileName}</span>
                        </div>
                      ) : (
                        <img
                          src={`${apiOrigin}/uploads/${fileName}`}
                          alt={`Preview ${idx}`}
                          className="image-thumbnail"
                        />
                      )}
                      <button
                        type="button"
                        className="btn btn-danger btn-sm remove-btn"
                        onClick={() => handleRemoveImage(fileName)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botões */}
            <div className="modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || uploading}
              >
                {submitting ? 'Salvando...' : 'Salvar Informativo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
